"use client";

/**
 * `TagsField` — free-form chip input. The form-state value is a
 * `string[]` of committed tags; the visible text input is local state
 * for the in-progress entry. Two commit triggers: pressing Enter, or
 * typing a `,` (which is a strong "I'm done with this tag" signal in
 * every tagging UI users have seen). Backspace on an empty input
 * removes the most recently added chip — the de-facto convention for
 * chip inputs in Gmail, Slack, GitHub, etc.
 *
 * Why not reuse {@link MultiSelectField} with an empty `options` array:
 * MultiSelect's UI is option-driven (popover with a filtered list of
 * preset choices). Tags are about free-form authoring; the input is the
 * primary control, not a popover trigger. The two fields look similar
 * because they both render chips, but their interaction models diverge.
 *
 * Trim + dedupe rules:
 * - Whitespace-only entries are ignored.
 * - Surrounding whitespace is trimmed (so `"  vat-21  "` becomes `"vat-21"`).
 * - Adding a tag that already exists in the array is a no-op (we don't
 *   want duplicate chips). Case-sensitive, since some tag systems are
 *   intentionally case-aware (e.g. SKU prefixes).
 *
 * State plumbing: Controller wraps the rhf binding so we get a stable
 * `string[]` value and can call `onChange` with new arrays. The local
 * `inputText` is plain `useState` — there's no rhf field for it because
 * uncommitted text is not part of the form payload.
 *
 * Accessibility: each chip has a Remove button with an `aria-label`
 * naming the tag ("Remove alpha"). The input is the labelled control
 * (FieldShell's `<Label htmlFor>` points to the input id), so screen
 * readers announce the field name when the user lands on the input.
 *
 * The visual layout puts the input INSIDE the chip-row container so
 * focus styling visually unites the wrapper. We render a flex-wrap row
 * containing the chips followed by the input itself, with a focus-within
 * ring on the wrapper — the input itself drops its own border/ring.
 */

import * as React from "react";
import { Controller } from "react-hook-form";
import { X } from "lucide-react";
import { cn } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { TagsFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface TagsFieldProps {
  field: TagsFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

function TagChip({
  label,
  onRemove,
  disabled,
}: {
  label: string;
  onRemove: () => void;
  disabled: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium text-fg">
      {label}
      <button
        type="button"
        aria-label={`Remove ${label}`}
        onClick={onRemove}
        disabled={disabled}
        className="rounded-sm p-0.5 text-fg-muted hover:bg-bg hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <X aria-hidden="true" className="size-3" />
      </button>
    </span>
  );
}

export function TagsField({ field, pathPrefix }: TagsFieldProps): React.ReactElement | null {
  const { form } = useFormContext();
  const [inputText, setInputText] = React.useState("");

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);
  const isDisabled = field.disabled === true;

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      <Controller
        name={path}
        control={form.control}
        render={({ field: rhf }) => {
          const tags = Array.isArray(rhf.value) ? (rhf.value as string[]) : [];

          const commit = (raw: string) => {
            const next = raw.trim();
            if (next.length === 0) return;
            if (tags.includes(next)) {
              setInputText("");
              return;
            }
            rhf.onChange([...tags, next]);
            setInputText("");
          };

          const removeAt = (index: number) => {
            rhf.onChange(tags.filter((_, i) => i !== index));
          };

          const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
              // Submit-on-Enter would close any wrapping <form>; we intercept
              // before that happens so Enter inside the tag input never
              // triggers a submission of the surrounding form.
              event.preventDefault();
              commit(inputText);
              return;
            }
            if (event.key === "Backspace" && inputText.length === 0 && tags.length > 0) {
              event.preventDefault();
              removeAt(tags.length - 1);
            }
          };

          const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            // A trailing comma is the second commit trigger — strip it and
            // commit the rest.
            if (value.endsWith(",")) {
              commit(value.slice(0, -1));
              return;
            }
            setInputText(value);
          };

          return (
            <div
              className={cn(
                "flex min-h-[var(--aio-input-height,2.75rem)] w-full flex-wrap items-center gap-1.5 rounded-md border border-border bg-bg px-3 py-1.5 text-sm text-fg",
                "transition-colors duration-fast ease-standard",
                "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-bg",
                errorMessage !== undefined && "border-danger focus-within:ring-danger",
                isDisabled && "cursor-not-allowed opacity-50",
              )}
            >
              {tags.map((tag, index) => (
                <TagChip
                  key={`${tag}-${String(index)}`}
                  label={tag}
                  disabled={isDisabled}
                  onRemove={() => {
                    removeAt(index);
                  }}
                />
              ))}
              <input
                id={path}
                type="text"
                value={inputText}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={(event) => {
                  // Commit on blur as well — users expect "click away to add".
                  commit(inputText);
                  rhf.onBlur();
                  // Preserve the synthetic event default behaviour (focus loss).
                  event.currentTarget.blur?.();
                }}
                ref={rhf.ref}
                aria-invalid={errorMessage !== undefined ? true : undefined}
                disabled={isDisabled}
                {...(field.placeholder !== undefined && tags.length === 0
                  ? { placeholder: field.placeholder }
                  : {})}
                className="flex-1 min-w-[8ch] bg-transparent outline-none placeholder:text-fg-muted disabled:cursor-not-allowed"
              />
            </div>
          );
        }}
      />
    </FieldShell>
  );
}
